import React, {useLayoutEffect, useRef} from "react";
import {abvNum} from "./CommonJS";
import {classNameScrollbarThin, LoadingAnim, tooltipIfNeeded} from "./CommonHtml";
import {componentInit, tailCheck, transitions, useRerender} from "./CommonReact";
import {icn} from "./UIIcons";
import {Button, TextInput} from "./Inputs";
import {UIPopPanel} from "./UIPopPanel";
import {UIVirtualTable} from "./UIVirtualTable";

// onSelect only triggers on list selections. onChange triggers on input box changes and list selection if no onSelect.

export function UIComboBox({
    field = "", value = undefined, optIdx = undefined, opts = undefined, optsVer = undefined, onPopPanel = undefined, item0 = undefined, onChange = undefined,
    onSelect = undefined, onPaging = undefined, sNoMatch = undefined, inputRef = undefined, placeholder = undefined, noCount = undefined,
    addClear = undefined, classNameWrapper = undefined, classNameListItem = undefined, className = undefined, stylePopPanel = undefined,
    inputProps = undefined, vtProps = undefined, onBlur = undefined, selectMode = undefined, selKeepOpen = undefined, noOptsFilter = undefined,
    initOpen = undefined, rowRenderer = undefined, autoWidth = undefined, onKeys = undefined, enableSearch = undefined, disabled = undefined,
    focusWhenCleared = false}) {
    const rerender = useRerender();
    let rAnchor = useRef();
    if (inputRef) rAnchor = inputRef;
    const vals = useRef({rPanel: {}, isOpen: initOpen}).current;
    let {vOpts} = vals;

    if (componentInit(vals, [value, optIdx, optsVer ?? JSON.stringify(opts)])) {
        if (opts) vals.isLoading = false;
        vOpts = opts?.[0]?.h !== undefined? opts : opts?.map(h => ({h, k: h}));       // opts can be passed as an array of strings
        if (item0) vOpts = [(item0?.h !== undefined ? item0 :  {h: item0}), ...vOpts || []];
        vals.allOpts = vals.vOpts = vOpts;

        vals.curIdx = typeof optIdx === "number"? optIdx :
            value !== undefined? vOpts?.findIndex(o => o?.k === value) ?? -1 : -1;
        if (selectMode && vals.curIdx === -1 && !placeholder) vals.curIdx = 0;
        vals.search = (vals.curIdx >= 0? vOpts?.[vals.curIdx]?.h : typeof value === "string"? value : "") ?? "";
        // if (field === "groupId") console.log({opts, item0, vOpts: vOpts, value, search: vals.search, curIdx: vals.curIdx})
    }

    if (componentInit(vals, [vals.search, vals.allOpts], "_init_search") &&
        !noOptsFilter && ((onChange && !selectMode) || vOpts?.[vals.curIdx]?.h !== vals.search)) {
        vOpts = !vals.search? vals.allOpts : vals.allOpts?.filter(o =>
            vals.search.toLowerCase().split(" ").filter(Boolean).every(srch => o?.h?.toLowerCase()?.includes(srch)));
        if (vals.curIdx !== -1 && vOpts?.length < vals.vOpts?.length)
            vals.curIdx = vOpts?.findIndex(o => o?.k === value);
        vals.vOpts = vOpts;
    }

    const show = show => {
        vals.rPanel.set(cur => ({...cur, show: vals.isOpen = show ?? !cur.show}));
        setTimeout(rerender, 50);     // update count display
    };

    const setIsLoading = loading => setTimeout(() => {
        vals.isLoadingPending = false;
        if (!loading === !vals.isLoading) return;
        vals.isLoading = !!loading;
        rerender();
    });

    function onEdit(f, v) {
        vals.search = v || "";
        if (v) show(true);
        if (selectMode && v === undefined) {
            vals.search = (vals.curIdx >= 0? vals.vOpts?.[vals.curIdx]?.h : typeof value === "string"? value : "") ?? "";
            if (value !== undefined)
                (onSelect || onChange)(field, undefined, -1, vals.isOpen);
            else
                rerender();
        } else if (!selectMode && onChange)
            onChange(field, v !== ""? v : undefined, vals.curIdx, vals.isOpen);
        else
            rerender();
    }

    function updateVals(idx, isSel) {
        if (!vals.isOpen)
            return show(true);
        else if (isSel && !selKeepOpen)
            show(false);
        const rowData = vOpts[idx];
        if (!rowData) return;
        if (isSel || selectMode)
            (onSelect || onChange)(field, rowData.k, idx, vals.isOpen);
        else if (vals.curIdx !== idx) {
            vals.curIdx = idx;
            rerender();
        }
    }

    const onSizing = props => ((vals.rowsFit = props.fRowsFit |0), onPaging?.(props));

    useLayoutEffect(() => {
        autoWidth && rAnchor.current?.setAttribute("size", rAnchor.current.value.length * 0.95 + 1|0);
    }, [vals.search]);

    const refocusInput = () => setTimeout(() => rAnchor.current?.focus());

    function UIPopPanelChildren() {
        if (!opts && !vals.isLoading && !vals.isLoadingPending) {
            onPopPanel && onPopPanel(value);
            setIsLoading(vals.isLoadingPending = true);
        }

        const noMatch = () => <div data-test={`${field}-no-match`} className="cursor-default select-none relative px-2 py-1 text-sm rounded bg-bg1 italic"> {sNoMatch ?? "No match."} </div>;

        return <div data-test={`${field}-ui-virtual-table-container`}> {vals.isLoading? null : !vOpts?.length? noMatch() :
            <div
                style={{minWidth: rAnchor?.current?.offsetWidth, height: Math.min(1.9 * vOpts.length, 12) + "rem", ...stylePopPanel}}
                className="overflow-hidden whitespace-pre rounded-lg bg-bg1 text-tx-cool-grey border border-tx-steel shadow-lg focus:outline-none resize">
                <UIVirtualTable
                    rows={vOpts}
                    rowRenderer={({style, rowData, index}) =>
                        <div
                            data-test={`${field}-ui-virtual-table-row`} data-row-index={rowData.k ?? 0} key={index} style={style}
                            className={tailCheck(classNameListItem,
                                                 "cursor-pointer flex items-center w-full transition hover:bg-prm-main hover:text-prm-contrast",
                                                 "text-sm", "px-2 py-1", vals.curIdx === index && "bg-prm-light text-prm-contrast", rowData.className)}
                            onMouseEnter={tooltipIfNeeded}
                            onClick={() => updateVals(index, true)}>
                            {rowRenderer?.(rowData, index) ?? rowData.h}
                        </div>}
                    scrollToIndex={vals.curIdx}
                    classNameGrid={classNameScrollbarThin}
                    onSizing={onSizing}
                    dataTest={field}
                    {...vtProps}/>
            </div>}</div>;
    }

    return (<>
        <TextInput
            ref={rAnchor}
            field={field}
            {...(selectMode && !enableSearch && {readOnly: true})}
            {...{classNameWrapper, disabled, ...inputProps}}
            className={tailCheck(
                `${className} ${inputProps?.className}`,
                ...vals.search && (!selectMode || (vals.search !== vOpts?.[0]?.h)) && !className?.match(/\b(p[xy]?-?\d*)\b/g)?
                    ["text-desc text-sm", "border border-desc", "bg-tx-steel/40"] : [])}
            {...(disabled || inputProps?.onClick) && {className: className + " opacity-50"}}
            onClick={inputProps?.onClick || (e => setTimeout(() =>
                (vals.vOpts?.length || onPopPanel) && e.target.selectionStart === e.target.selectionEnd && show(), 100))}   // if double-click selection, prevent popup.
            value={vals.search}
            placeholder={placeholder}
            onKeys={{
                ...onKeys,
                Enter: (e, unhandle) => vals.isOpen? updateVals(vals.curIdx ?? 0, true) : onKeys?.Enter?.(e) ?? unhandle(),
                Escape: (e, unhandle) => vals.isOpen || !onKeys?.Escape? unhandle() : onKeys?.Escape?.(e),
                ArrowUp: () => updateVals(((vals.curIdx ?? 0) || vOpts.length) - 1),
                ArrowDown: () => updateVals(((vals.curIdx ?? -1) + 1) % vOpts.length),
                Home: (e, unhandle) => !vals.isOpen ? unhandle() : updateVals(0),
                End: (e, unhandle) => !vals.isOpen ? unhandle() : updateVals(vOpts?.length - 1),
                PageUp: (e, unhandle) => !vals.isOpen ? unhandle() : updateVals(Math.max((vals.curIdx ?? 0) - vals.rowsFit, 0)),
                PageDown: (e, unhandle) => !vals.isOpen ? unhandle() : updateVals(Math.min((vals.curIdx ?? -1) + vals.rowsFit, vOpts.length-1)),
            }}
            btnRight={!disabled && !inputProps?.onClick && <>
                {vals.isLoading && <LoadingAnim/>}
                {!noCount && vOpts && vals.isOpen &&
                    <div data-test="total-items" className="-mt-[2px] mr-1 text-xs text-subtitle2 italic font-sans" title={`Total items: ${vOpts.length.toLocaleString()}`}>
                        ({abvNum(vOpts.length)})
                    </div>}
                {vals.search && (addClear ?? true) && (!selectMode || (addClear && (!item0 || vals.search !== vOpts?.[0]?.h))) &&
                    <Button data-test="clear-input" onClick={() => (onEdit(field, undefined), focusWhenCleared && refocusInput())} tabIndex={-1}>{icn.remove3}</Button>}
                <Button onClick={() => show()} tabIndex={-1} onFocus={refocusInput}>{icn.picker}</Button>
            </>}
            onChange={onEdit}
            onBlur={onBlur && (e => {
                if (vals.isOpen)            // if panel was open just return the focus to the input. But first allow focus to switch to destination
                    refocusInput();
                else if (!e.relatedTarget || !e.target.parentElement.contains(e.relatedTarget))     // ignore blur if clicked on descendant of parent. note that relatedTarget (onBlur destination) is null if element is not tabIndex 0+
                    onBlur(e);
            })}/>
        <UIPopPanel
            {...{rAnchor}} ctrl={vals.rPanel} margin={2} transition={transitions.wipeDownHide} show={vals.isOpen}
            className="shadow-lg ring-1 ring-black z-[101] ring-opacity-5"
            onClose={() => show(false)}
            children={UIPopPanelChildren}/>
    </>);
}
